import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "../../lib/api";
import {
  Sparkles, Send, Loader2, CalendarCheck, Banknote, Zap, Car, ArrowRight, Check,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Calendar, Users, FileText, Plus, Square,
  GalleryHorizontalEnd, Rows3, Sunrise,
  PanelLeftClose, PanelLeftOpen, MessageSquarePlus,
} from "lucide-react";

const SHORTCUTS = [
  { action: "calendar", label: "Calendrier", icon: Calendar },
  { action: "bookings", label: "Réservations", icon: CalendarCheck },
  { action: "clients", label: "Clients", icon: Users },
  { action: "fleet", label: "Véhicules", icon: Car },
  { action: "quotes", label: "Devis", icon: FileText },
  { action: "revenue", label: "Revenus", icon: Banknote },
];

const ACTION_LABELS = {
  calendar: "Mon planning du jour",
  today_bookings: "Réservations du jour",
  bookings: "Mes réservations",
  clients: "Mes clients",
  quotes: "Mes devis",
  revenue: "Mes revenus",
  tasks: "Mes tâches recommandées",
  tasks_done: "Mes tâches terminées",
  fleet: "Ma flotte",
};

const CARD_ICON = { booking: CalendarCheck, revenue: Banknote, task: Zap, vehicle: Car, client: Users, quote: FileText, create: Plus };
const BADGE_LABEL = {
  confirmed: "Confirmée", pending: "En attente", completed: "Terminée", cancelled: "Annulée",
  in_progress: "En cours", "terminée": "Terminée",
  high: "Urgent", medium: "Important", low: "Info",
  active: "Disponible", rented: "En location", maintenance: "Entretien", inactive: "Inactif",
  draft: "Brouillon", sent: "Envoyé", accepted: "Accepté", refused: "Refusé", declined: "Refusé", expired: "Expiré",
};
const BADGE_CLASS = {
  confirmed: "bg-emerald-500/15 text-emerald-300", pending: "bg-amber-500/15 text-amber-300",
  completed: "bg-slate-500/20 text-slate-300", cancelled: "bg-red-500/15 text-red-300",
  in_progress: "bg-sky-500/15 text-sky-300", "terminée": "bg-emerald-500/15 text-emerald-300",
  late: "bg-red-500/15 text-red-300",
  high: "bg-red-500/15 text-red-300", medium: "bg-amber-500/15 text-amber-300", low: "bg-slate-500/20 text-slate-300",
  active: "bg-emerald-500/15 text-emerald-300", rented: "bg-sky-500/15 text-sky-300", maintenance: "bg-amber-500/15 text-amber-300", inactive: "bg-slate-500/20 text-slate-300",
  draft: "bg-slate-500/20 text-slate-300", sent: "bg-violet-500/15 text-violet-300", accepted: "bg-emerald-500/15 text-emerald-300",
  refused: "bg-red-500/15 text-red-300", declined: "bg-red-500/15 text-red-300", expired: "bg-slate-500/20 text-slate-300",
};

function renderBold(text) {
  return String(text).split("**").map((part, i) => (i % 2 === 1 ? <strong key={i} className="font-semibold">{part}</strong> : part));
}

// ─── Gestionnaire central des actions Copilot ───
// Les URLs internes du Pilotage doivent forcer la sortie du mode Copilote (mode=junago),
// sinon la navigation change l'URL mais le workspace Copilot plein écran masque la page cible.
export function resolveCopilotUrl(url) {
  if (!url || typeof url !== "string" || !url.startsWith("/")) return null;
  if (url.startsWith("/vendor/pilotage") && !url.includes("mode=") && !url.includes("copilot=")) {
    return url + (url.includes("?") ? "&" : "?") + "mode=junago";
  }
  return url;
}

function handleCopilotAction(navigate, url) {
  const target = resolveCopilotUrl(url);
  if (!target) {
    toast.error("Cette action n'est pas disponible pour le moment.");
    return;
  }
  if (!target.includes("copilot=")) {
    // Navigation qui sort du Copilote : mémoriser pour « Retour au Copilote » + actualisation des cartes
    sessionStorage.setItem("jn_from_copilot", "1");
    sessionStorage.setItem("jn_copilot_refresh", "1");
  }
  navigate(target);
}

function CardVignette({ card, onTasksChanged, delay = 0 }) {
  const navigate = useNavigate();
  const [done, setDone] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [decision, setDecision] = useState(null); // "confirmed" | "cancelled"
  const [deciding, setDeciding] = useState(null);
  const Icon = CARD_ICON[card.type] || Sparkles;
  const badge = decision || card.badge;

  // Navigation vers la véritable page métier du tableau de bord (jamais de pop-up dans la conversation)
  const open = () => handleCopilotAction(navigate, card.action_url);

  const dismiss = async () => {
    setDismissing(true);
    try {
      await api.post(`/vendor/tasks/system/${card.id}/dismiss`);
      setDone(true);
      onTasksChanged?.();
    } finally {
      setDismissing(false);
    }
  };

  const decide = async (status) => {
    setDeciding(status);
    try {
      await api.patch(`/vendor/bookings/${card.id}`, { status });
      setDecision(status);
      onTasksChanged?.();
    } catch {
      // silencieux : badge inchangé
    } finally {
      setDeciding(null);
    }
  };

  return (
    <div
      className={`rounded-xl bg-white/[0.06] border border-white/10 p-3.5 transition-opacity h-full flex flex-col ${done ? "opacity-50" : ""}`}
      style={{ animation: "jnMaterialize 0.45s cubic-bezier(0.22,1,0.36,1) both" }}
      data-testid={`copilot-card-${card.type}`}
    >
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-lg bg-violet-600/25 border border-violet-400/25 flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-violet-300" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[13px] font-semibold text-white ${done ? "line-through" : ""}`}>{card.title}</span>
            {badge && (BADGE_LABEL[badge] || card.badge_label) && (
              <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${BADGE_CLASS[badge] || "bg-slate-500/20 text-slate-300"}`}>
                {decision ? BADGE_LABEL[decision] : (BADGE_LABEL[badge] || card.badge_label)}
              </span>
            )}
          </div>
          {card.value && <div className="mt-0.5 font-heading text-2xl font-semibold text-white">{card.value}</div>}
          {card.subtitle && <div className="mt-0.5 text-[11px] text-slate-400 leading-relaxed">{card.subtitle}</div>}
        </div>
      </div>
      <div className="mt-auto pt-3 flex items-center gap-2 flex-wrap">
        {card.can_decide && !decision && (
          <>
            <button
              onClick={() => decide("confirmed")}
              disabled={!!deciding}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-semibold transition-colors disabled:opacity-50"
              data-testid="copilot-card-accept"
            >
              {deciding === "confirmed" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Accepter
            </button>
            <button
              onClick={() => decide("cancelled")}
              disabled={!!deciding}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-400/30 text-red-300 hover:bg-red-500/10 text-[11px] font-semibold transition-colors disabled:opacity-50"
              data-testid="copilot-card-refuse"
            >
              {deciding === "cancelled" ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Refuser
            </button>
          </>
        )}
        {decision && (
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${decision === "confirmed" ? "text-emerald-300" : "text-red-300"}`}>
            <Check className="h-3 w-3" /> {decision === "confirmed" ? "Réservation acceptée" : "Réservation refusée"}
          </span>
        )}
        {card.action_url && (
          <button
            onClick={open}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-colors ${
              card.can_decide && !decision
                ? "border border-white/15 text-slate-300 hover:text-white hover:border-white/30"
                : "bg-violet-600 hover:bg-violet-500 text-white"
            }`}
            data-testid="copilot-card-open"
          >
            {card.action_label || "Ouvrir"} <ArrowRight className="h-3 w-3" />
          </button>
        )}
        {(card.actions || []).map((a, i) => a?.url && (
          <button
            key={i}
            onClick={() => handleCopilotAction(navigate, a.url)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/15 text-slate-300 hover:text-white hover:border-white/30 text-[11px] font-semibold transition-colors"
            data-testid="copilot-card-secondary"
          >
            {a.label}
          </button>
        ))}
        {card.dismissable && !done && (
          <button
            onClick={dismiss}
            disabled={dismissing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/15 text-slate-300 hover:text-white hover:border-white/30 text-[11px] font-semibold transition-colors disabled:opacity-50"
            data-testid="copilot-card-dismiss"
          >
            {dismissing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Marquer comme terminée
          </button>
        )}
        {done && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-300">
            <Check className="h-3 w-3" /> Terminée
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Résultats enrichis : texte d'abord, cartes masquées derrière un bouton premium ───
const GROUP_NOUN = {
  booking: ["la réservation", "réservations"],
  client: ["le client", "clients"],
  vehicle: ["le véhicule", "véhicules"],
  quote: ["le devis", "devis"],
  task: ["la tâche", "tâches"],
  revenue: ["le résultat", "résultats"],
};
const LIST_GROUP = { "list-bookings": "booking", "list-clients": "client", "list-vehicles": "vehicle", "list-quotes": "quote", "list-tasks": "task" };

function ViewAllButton({ card }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => handleCopilotAction(navigate, card.action_url)}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-violet-400/30 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20 hover:border-violet-400/50 text-[11px] font-semibold transition-colors"
      data-testid="copilot-viewall-btn"
    >
      {card.title} <ArrowRight className="h-3 w-3" />
    </button>
  );
}

function RevealGroup({ type, cards, viewAll, single, customLabel, onTasksChanged }) {
  const [open, setOpen] = useState(false);
  const [view, setViewState] = useState(() => localStorage.getItem("jn_copilot_card_view") || "h");
  const setView = (v) => { setViewState(v); localStorage.setItem("jn_copilot_card_view", v); };
  const [idx, setIdx] = useState(0);
  const railRef = useRef(null);
  const n = cards.length;
  const noun = GROUP_NOUN[type] || ["le résultat", "résultats"];
  const label = customLabel || (n === 1
    ? `Afficher ${single ? "le résultat" : noun[0]}`
    : `Afficher les ${n} ${single ? "résultats" : noun[1]}`);
  const groupId = `copilot-group-${type}-${customLabel ? customLabel.length : "r"}`;

  const onScroll = () => {
    const el = railRef.current;
    if (!el || !el.firstElementChild) return;
    const w = el.firstElementChild.offsetWidth + 10;
    setIdx(Math.max(0, Math.min(n - 1, Math.round(el.scrollLeft / w))));
  };
  const scrollByCard = (dir) => {
    const el = railRef.current;
    if (!el || !el.firstElementChild) return;
    el.scrollBy({ left: dir * (el.firstElementChild.offsetWidth + 10), behavior: "smooth" });
  };

  return (
    <div data-testid="copilot-results-group">
      <div className="flex items-center gap-2 flex-wrap min-h-[36px]">
        <button
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls={groupId}
          className="jn-reveal-btn inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[12px] font-bold text-violet-100"
          data-testid="copilot-results-toggle"
        >
          <Sparkles className="h-3.5 w-3.5 text-violet-300" aria-hidden="true" />
          {open ? "Masquer les résultats" : label}
          {open ? <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />}
        </button>
        {open && (
          <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] p-0.5" role="group" aria-label="Mode d'affichage des résultats">
            <button
              onClick={() => setView("h")}
              title="Vue horizontale (carrousel)"
              aria-label="Vue horizontale (carrousel)"
              aria-pressed={view === "h"}
              className={`h-7 w-9 rounded-full flex items-center justify-center transition-colors ${view === "h" ? "bg-violet-600 text-white" : "text-slate-400 hover:text-slate-200"}`}
              data-testid="copilot-view-horizontal"
            >
              <GalleryHorizontalEnd className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setView("v")}
              title="Vue verticale (liste)"
              aria-label="Vue verticale (liste)"
              aria-pressed={view === "v"}
              className={`h-7 w-9 rounded-full flex items-center justify-center transition-colors ${view === "v" ? "bg-violet-600 text-white" : "text-slate-400 hover:text-slate-200"}`}
              data-testid="copilot-view-vertical"
            >
              <Rows3 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        {open && view === "h" && n > 1 && (
          <span className="text-[10px] font-bold text-slate-500 tabular-nums" aria-live="polite" data-testid="copilot-carousel-counter">
            {idx + 1} / {n}
          </span>
        )}
      </div>
      {open && (
        <div id={groupId} className="mt-2" style={{ animation: "jnMaterialize 0.3s cubic-bezier(0.22,1,0.36,1) both" }}>
          {view === "h" ? (
            <div className="relative">
              <div ref={railRef} onScroll={onScroll} className="jn-carousel flex gap-2.5 overflow-x-auto pb-1" data-testid="copilot-carousel">
                {cards.map((c, j) => (
                  <div key={j} className="shrink-0 w-[82%] sm:w-[320px] flex">
                    <CardVignette card={c} onTasksChanged={onTasksChanged} />
                  </div>
                ))}
              </div>
              {n > 1 && (
                <>
                  <button
                    onClick={() => scrollByCard(-1)}
                    aria-label="Carte précédente"
                    className="hidden md:flex absolute -left-3 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-[#191930] border border-white/15 text-slate-300 hover:text-white hover:border-violet-400/50 items-center justify-center shadow-lg transition-colors"
                    data-testid="copilot-carousel-prev"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => scrollByCard(1)}
                    aria-label="Carte suivante"
                    className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-[#191930] border border-white/15 text-slate-300 hover:text-white hover:border-violet-400/50 items-center justify-center shadow-lg transition-colors"
                    data-testid="copilot-carousel-next"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {cards.map((c, j) => (
                <CardVignette key={j} card={c} onTasksChanged={onTasksChanged} />
              ))}
            </div>
          )}
          {viewAll && <div className="mt-2"><ViewAllButton card={viewAll} /></div>}
        </div>
      )}
    </div>
  );
}

function CardGroup({ cards, compact, onTasksChanged }) {
  // Séparation : cartes d'action (créer X) affichées directement · « voir tous » rattachés
  // à leur groupe · résultats groupés par entité, masqués par défaut (max 10 par groupe)
  const direct = [];
  const lists = {};
  const globalLists = [];
  const grouped = {};
  cards.forEach((c) => {
    if (c.type === "create" && String(c.id || "").startsWith("list-")) {
      const g = LIST_GROUP[c.id];
      if (g) lists[g] = c;
      else globalLists.push(c);
    } else if (c.type === "create") {
      direct.push(c);
    } else {
      (grouped[c.type] = grouped[c.type] || []).push(c);
    }
  });
  const groups = Object.entries(grouped).map(([type, cs]) => [type, cs.slice(0, 10)]);
  const single = groups.length === 1;
  return (
    <div className={`space-y-2 ${compact ? "" : "max-w-[85%]"}`}>
      {direct.map((c, j) => (
        <CardVignette key={`d${j}`} card={c} onTasksChanged={onTasksChanged} />
      ))}
      {groups.map(([type, cs]) => (
        <RevealGroup key={type} type={type} cards={cs} viewAll={lists[type]} single={single} onTasksChanged={onTasksChanged} />
      ))}
      {globalLists.map((c, j) => (
        <ViewAllButton key={`g${j}`} card={c} />
      ))}
    </div>
  );
}

// ─── Suggestions intelligentes sous chaque réponse ───
function SuggestionChips({ suggestions, onAction, onPrompt }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-wrap gap-1.5 max-w-[85%] pt-0.5" data-testid="copilot-suggestions">
      {suggestions.map((s, i) => (
        <button
          key={i}
          onClick={() => {
            if (s.action) onAction(s.action);
            else if (s.url) handleCopilotAction(navigate, s.url);
            else if (s.prompt) onPrompt(s.prompt);
          }}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-violet-400/25 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20 hover:border-violet-400/45 text-[11px] font-semibold transition-colors"
          data-testid="copilot-suggestion-chip"
        >
          <Sparkles className="h-2.5 w-2.5 opacity-60" /> {s.label}
        </button>
      ))}
    </div>
  );
}

function Bubble({ role, streaming, children }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={isUser ? "copilot-bubble-user" : "copilot-bubble-assistant"}
        className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${
          isUser ? "bg-violet-600 text-white rounded-br-md" : "bg-white/[0.07] border border-white/10 text-slate-200 rounded-bl-md"
        }`}
      >
        {typeof children === "string" ? renderBold(children) : children}
        {streaming && <span className="jn-caret" aria-hidden="true" />}
      </div>
    </div>
  );
}

// ─── Indicateur d'étape premium : une seule étape active, hauteur stable, accessible ───
function StatusIndicator({ label }) {
  return (
    <div className="flex items-center gap-2 h-6 pl-1" aria-live="polite" role="status" data-testid="copilot-status">
      <Sparkles className="h-3.5 w-3.5 text-violet-400 animate-pulse shrink-0" />
      <span className="text-[12px] text-violet-200/90 font-medium">{label}</span>
      <span className="inline-flex items-center gap-1" aria-hidden="true">
        {[0, 1, 2].map((d) => (
          <span key={d} className="h-1 w-1 rounded-full bg-violet-400/80 animate-bounce" style={{ animationDelay: `${d * 150}ms` }} />
        ))}
      </span>
    </div>
  );
}

// ─── Brief matinal proactif : synthèse du jour, chargée à l'ouverture du chat ───
export function MorningBrief({ onTasksChanged }) {
  const [brief, setBrief] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/vendor/copilot/briefing").then((r) => { if (alive) setBrief(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!brief) return null;
  return (
    <div className="w-full max-w-lg text-left space-y-2.5 mt-7" data-testid="copilot-briefing">
      <div className="rounded-2xl bg-gradient-to-br from-violet-600/15 to-fuchsia-600/10 border border-violet-400/25 p-4">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-white">
          <Sunrise className="h-4 w-4 text-violet-300" aria-hidden="true" /> {brief.greeting}
        </div>
        <p className="mt-1.5 text-[12px] text-slate-300 leading-relaxed" data-testid="copilot-briefing-summary">{brief.summary}</p>
      </div>
      {(brief.groups || []).map((g) => (
        <RevealGroup key={g.key} type="booking" cards={g.cards} customLabel={g.label} single={false} onTasksChanged={onTasksChanged} />
      ))}
    </div>
  );
}

export function Conversation({ compact = false, onTasksChanged }) {
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("copilot_chat_messages")) || []; } catch { return []; }
  });
  const [input, setInput] = useState(() => sessionStorage.getItem("copilot_chat_draft") || "");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const sendingRef = useRef(false);
  const sendRef = useRef(null);
  const abortRef = useRef(null);
  const sessionRef = useRef(sessionStorage.getItem("copilot_chat_session") || null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Persistance : la conversation survit aux allers-retours vers le mode Classique
  // (les statuts temporaires de streaming ne sont jamais conservés dans l'historique)
  useEffect(() => {
    try {
      const clean = messages.slice(-40).map(({ status, streaming, ...m }) => m);
      sessionStorage.setItem("copilot_chat_messages", JSON.stringify(clean));
    } catch { /* quota */ }
  }, [messages]);
  useEffect(() => {
    sessionStorage.setItem("copilot_chat_draft", input);
  }, [input]);

  const resetConversation = () => {
    setMessages([]);
    setInput("");
    sessionRef.current = null;
    sessionStorage.removeItem("copilot_chat_messages");
    sessionStorage.removeItem("copilot_chat_draft");
    sessionStorage.removeItem("copilot_chat_session");
  };

  // Écoute les actions déclenchées de l'extérieur (sidebar, rail, TaskCenter)
  useEffect(() => {
    const handler = (e) => {
      const action = e.detail;
      if (action && ACTION_LABELS[action]) sendRef.current?.({ action, label: ACTION_LABELS[action] });
    };
    window.addEventListener("copilot-run-action", handler);
    return () => window.removeEventListener("copilot-run-action", handler);
  }, []);

  // Nouvelle conversation depuis la sidebar
  useEffect(() => {
    const handler = () => resetConversation();
    window.addEventListener("copilot-new-conversation", handler);
    return () => window.removeEventListener("copilot-new-conversation", handler);
  }, []);

  // Brief du jour à la demande (sidebar ou bouton de la barre de saisie)
  const showBriefing = async () => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    try {
      const r = await api.get("/vendor/copilot/briefing");
      setMessages((m) => [...m, {
        role: "assistant",
        content: `**${r.data.greeting}** ${r.data.summary}`,
        cards: [], suggestions: [], briefGroups: r.data.groups || [],
      }]);
    } catch {
      toast.error("Impossible de charger le brief du jour.");
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };
  const briefRef = useRef(null);
  briefRef.current = showBriefing;
  useEffect(() => {
    const handler = () => briefRef.current?.();
    window.addEventListener("copilot-show-briefing", handler);
    return () => window.removeEventListener("copilot-show-briefing", handler);
  }, []);

  // Retour au Copilote après une action métier : ré-actualise les cartes du dernier message
  // (les données peuvent avoir changé — paiement, signature, départ, retour…)
  useEffect(() => {
    if (sessionStorage.getItem("jn_copilot_refresh") !== "1") return;
    sessionStorage.removeItem("jn_copilot_refresh");
    const collect = (cards) => (cards || [])
      .filter((c) => ["booking", "client", "vehicle", "quote"].includes(c.type) && c.id)
      .map((c) => ({ type: c.type, id: c.id }));
    let idx = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (collect(messages[i].cards).length || (messages[i].briefGroups || []).some((g) => collect(g.cards).length)) { idx = i; break; }
    }
    if (idx === -1) return;
    const msg = messages[idx];
    const refs = [...collect(msg.cards), ...(msg.briefGroups || []).flatMap((g) => collect(g.cards))];
    api.post("/vendor/copilot/cards/refresh", { refs }).then((r) => {
      const byKey = Object.fromEntries((r.data.cards || []).map((c) => [`${c.type}:${c.id}`, c]));
      const upd = (cards) => (cards || []).map((c) => byKey[`${c.type}:${c.id}`] || c);
      setMessages((ms) => ms.map((m, i) => i !== idx ? m : {
        ...m,
        cards: m.cards ? upd(m.cards) : m.cards,
        briefGroups: m.briefGroups ? m.briefGroups.map((g) => ({ ...g, cards: upd(g.cards) })) : m.briefGroups,
        refreshed: true,
      }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Injection de texte externe (ex : bouton « Analyser avec Junago Copilot » du module Statistiques)
  useEffect(() => {
    const handler = (e) => {
      if (typeof e.detail === "string" && e.detail.trim()) sendRef.current?.({ text: e.detail });
    };
    window.addEventListener("copilot-send-text", handler);
    return () => window.removeEventListener("copilot-send-text", handler);
  }, []);

  const send = async ({ text, action, label } = {}) => {
    if (sendingRef.current) return;
    const userText = label || (text || input).trim();
    if (!userText) return;
    setInput("");
    sendingRef.current = true;
    setSending(true);

    // ── Raccourci déterministe : POST classique (réponse quasi instantanée) ──
    if (action) {
      setMessages((m) => [...m, { role: "user", content: userText }]);
      try {
        const r = await api.post("/vendor/copilot/chat", { action, session_id: sessionRef.current });
        sessionRef.current = r.data.session_id;
        sessionStorage.setItem("copilot_chat_session", r.data.session_id);
        setMessages((m) => [...m, { role: "assistant", content: r.data.reply, cards: r.data.cards || [], suggestions: r.data.suggestions || [] }]);
      } catch (err) {
        setMessages((m) => [...m, { role: "assistant", content: err.response?.data?.detail || "Une erreur est survenue, réessayez." }]);
      } finally {
        sendingRef.current = false;
        setSending(false);
      }
      return;
    }

    // ── Message libre : streaming SSE (statuts réels → texte progressif → cartes) ──
    setMessages((m) => [
      ...m,
      { role: "user", content: userText },
      { role: "assistant", content: "", cards: [], suggestions: [], streaming: true, status: "Analyse de votre demande…" },
    ]);
    const updateLast = (fn) => setMessages((m) => {
      const c = [...m];
      c[c.length - 1] = fn(c[c.length - 1]);
      return c;
    });
    const controller = new AbortController();
    abortRef.current = controller;
    let gotTerminal = false;
    try {
      const res = await fetch(`${API}/vendor/copilot/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("cm_token")}`,
        },
        body: JSON.stringify({ message: userText, session_id: sessionRef.current }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) throw new Error("stream");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          let ev;
          try { ev = JSON.parse(line.slice(5)); } catch { continue; }
          if (ev.type === "session") {
            sessionRef.current = ev.session_id;
            sessionStorage.setItem("copilot_chat_session", ev.session_id);
          } else if (ev.type === "status") {
            updateLast((l) => ({ ...l, status: ev.label }));
          } else if (ev.type === "text_delta") {
            updateLast((l) => ({ ...l, content: l.content + ev.content, status: null }));
          } else if (ev.type === "cards") {
            updateLast((l) => ({ ...l, cards: ev.items || [], status: null }));
          } else if (ev.type === "suggestions") {
            updateLast((l) => ({ ...l, suggestions: ev.items || [] }));
          } else if (ev.type === "complete") {
            gotTerminal = true;
            updateLast((l) => ({ ...l, streaming: false, status: null, content: l.content.trim() }));
          } else if (ev.type === "error") {
            gotTerminal = true;
            updateLast((l) => ({
              ...l, streaming: false, status: null,
              content: l.content.trim() ? `${l.content.trim()}\n\n⚠ ${ev.message}` : ev.message,
            }));
          }
        }
      }
      if (!gotTerminal) {
        updateLast((l) => ({
          ...l, streaming: false, status: null,
          content: l.content.trim() ? `${l.content.trim()} — (réponse incomplète)` : "La connexion a été interrompue, réessayez.",
        }));
      }
    } catch (err) {
      if (err.name === "AbortError") {
        updateLast((l) => ({
          ...l, streaming: false, status: null,
          content: l.content.trim() ? `${l.content.trim()} — (réponse interrompue)` : "Réponse interrompue.",
        }));
      } else {
        updateLast((l) => ({ ...l, streaming: false, status: null, content: l.content.trim() || "Une erreur est survenue, réessayez." }));
      }
    } finally {
      abortRef.current = null;
      sendingRef.current = false;
      setSending(false);
    }
  };
  sendRef.current = send;

  const stop = () => abortRef.current?.abort();

  const empty = messages.length === 0;
  const lastIdx = messages.length - 1;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Zone messages */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-4 space-y-3" data-testid="copilot-messages">
        {empty ? (
          <div className={`min-h-full py-4 flex flex-col items-center justify-center text-center ${compact ? "px-2" : "px-6"}`}>
            <div className={`font-heading font-semibold text-white ${compact ? "text-base" : "text-2xl"}`}>Junago Copilot</div>
            <p className={`mt-1.5 text-slate-400 leading-relaxed max-w-sm ${compact ? "text-[11px]" : "text-sm"}`}>
              Je connais votre flotte, vos réservations et vos revenus — et je peux lancer toutes vos actions.
              Essayez « créer une réservation » ou utilisez un raccourci.
            </p>
            <MorningBrief onTasksChanged={onTasksChanged} />
            {!compact && (
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3 w-full max-w-lg">
                {SHORTCUTS.map((s) => (
                  <button
                    key={s.action}
                    onClick={() => send({ action: s.action, label: s.label })}
                    className="rounded-2xl bg-white/[0.05] border border-white/10 hover:border-violet-400/40 hover:bg-white/[0.08] p-4 text-left transition-colors group"
                    data-testid={`copilot-hero-shortcut-${s.action}`}
                  >
                    <s.icon className="h-4 w-4 text-violet-300 group-hover:text-violet-200" />
                    <div className="mt-2 text-[12px] font-semibold text-slate-200">{s.label}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={i} className="space-y-2">
              {(m.content || !m.streaming) && (
                <Bubble role={m.role} streaming={m.streaming && !!m.content}>{m.content}</Bubble>
              )}
              {m.status && <StatusIndicator label={m.status} />}
              {m.cards?.length > 0 && (
                <CardGroup cards={m.cards} compact={compact} onTasksChanged={onTasksChanged} />
              )}
              {m.briefGroups?.length > 0 && (
                <div className={`space-y-2 ${compact ? "" : "max-w-[85%]"}`}>
                  {m.briefGroups.map((g) => (
                    <RevealGroup key={g.key} type="booking" cards={g.cards} customLabel={g.label} single={false} onTasksChanged={onTasksChanged} />
                  ))}
                </div>
              )}
              {m.refreshed && (
                <div className="inline-flex items-center gap-1.5 text-[10px] text-slate-500 pl-1" data-testid="copilot-cards-refreshed">
                  <Check className="h-3 w-3 text-emerald-400" /> Données actualisées après votre passage dans le tableau de bord.
                </div>
              )}
              {m.suggestions?.length > 0 && i === lastIdx && !m.streaming && !sending && (
                <SuggestionChips
                  suggestions={m.suggestions}
                  onAction={(a) => send({ action: a, label: ACTION_LABELS[a] || a })}
                  onPrompt={(p) => send({ text: p })}
                />
              )}
            </div>
          ))
        )}
        {sending && !messages[lastIdx]?.streaming && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-md bg-white/[0.07] border border-white/10 px-4 py-3 inline-flex items-center gap-1.5">
              {[0, 1, 2].map((d) => (
                <span key={d} className="h-1.5 w-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: `${d * 150}ms` }} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Saisie */}
      <div className="shrink-0 border-t border-white/10 px-3 pt-3 pb-3">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2">
          <button
            type="button"
            onClick={showBriefing}
            disabled={sending}
            title="Brief du jour"
            aria-label="Afficher le brief du jour"
            className="h-10 w-10 rounded-xl border border-violet-400/25 bg-violet-500/10 hover:bg-violet-500/20 hover:border-violet-400/50 disabled:opacity-40 flex items-center justify-center text-violet-300 transition-colors shrink-0"
            data-testid="copilot-briefing-btn"
          >
            <Sunrise className="h-4 w-4" />
          </button>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Posez une question ou demandez une action : « créer un devis »…"
            className="flex-1 min-w-0 rounded-xl bg-white/[0.06] border border-white/10 px-3.5 py-2.5 text-[13px] text-white placeholder:text-slate-500 outline-none focus:border-violet-400/50"
            data-testid="copilot-chat-input"
          />
          {sending && messages[lastIdx]?.streaming ? (
            <button
              type="button"
              onClick={stop}
              title="Arrêter la réponse"
              className="h-10 w-10 rounded-xl border border-white/15 hover:border-red-400/50 hover:bg-red-500/10 flex items-center justify-center text-slate-300 hover:text-red-300 transition-colors shrink-0"
              data-testid="copilot-chat-stop"
            >
              <Square className="h-3.5 w-3.5 fill-current" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="h-10 w-10 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-40 flex items-center justify-center text-white transition-colors shrink-0"
              data-testid="copilot-chat-send"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

// ── Mode plein écran (onglet Junago Copilot) — avec barre latérale rétractable ──
export function CopilotWorkspace({ onTasksChanged }) {
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem("jn_copilot_sidebar");
    if (saved) return saved === "collapsed";
    return typeof window !== "undefined" && window.innerWidth < 768;
  });
  const toggle = () =>
    setCollapsed((c) => {
      localStorage.setItem("jn_copilot_sidebar", !c ? "collapsed" : "open");
      return !c;
    });
  const isCollapsed = collapsed;
  const runAction = (a) => window.dispatchEvent(new CustomEvent("copilot-run-action", { detail: a }));
  const newConversation = () => window.dispatchEvent(new Event("copilot-new-conversation"));

  return (
    <div className="h-full relative bg-[#0B0B16] overflow-hidden flex" data-testid="copilot-workspace">
      {/* ── Barre latérale gauche rétractable (desktop uniquement — les raccourcis mobiles sont dans l'écran d'accueil du chat) ── */}
      <aside
        className={`h-full border-r border-white/10 bg-[#0D0D1A]/95 backdrop-blur flex-col overflow-hidden transition-[width] duration-300 hidden md:flex shrink-0 ${collapsed ? "md:w-[56px]" : "md:w-60"}`}
        style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}
        data-testid="copilot-sidebar"
      >
        <div className={`flex items-center h-12 px-2.5 shrink-0 ${isCollapsed ? "justify-center" : "justify-between"}`}>
          {!isCollapsed && (
            <span className="pl-1 text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 whitespace-nowrap">Raccourcis</span>
          )}
          <button
            onClick={toggle}
            data-testid="copilot-sidebar-toggle"
            title={isCollapsed ? "Déplier la barre" : "Réduire la barre"}
            aria-label={isCollapsed ? "Déplier la barre latérale" : "Réduire la barre latérale"}
            className="h-8 w-8 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 flex items-center justify-center transition-colors shrink-0"
          >
            {isCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </div>
        <div className="px-2 shrink-0">
          <button
            onClick={newConversation}
            data-testid="copilot-sidebar-new"
            title="Nouvelle conversation"
            className={`w-full flex items-center gap-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-[12px] font-bold transition-colors h-10 ${isCollapsed ? "justify-center" : "px-3"}`}
          >
            <MessageSquarePlus className="h-4 w-4 shrink-0" />
            {!isCollapsed && <span className="whitespace-nowrap">Nouvelle conversation</span>}
          </button>
        </div>
        <nav className="mt-3 px-2 space-y-0.5 flex-1 overflow-y-auto no-scrollbar">
          <button
            onClick={() => window.dispatchEvent(new Event("copilot-show-briefing"))}
            title="Brief du jour"
            data-testid="copilot-sidebar-briefing"
            className={`w-full flex items-center gap-2.5 rounded-xl text-[12px] font-semibold text-violet-200 hover:text-white hover:bg-violet-500/15 transition-colors h-10 ${isCollapsed ? "justify-center" : "px-3"}`}
          >
            <Sunrise className="h-4 w-4 shrink-0 text-violet-300" />
            {!isCollapsed && <span className="whitespace-nowrap">Brief du jour</span>}
          </button>
          {SHORTCUTS.map((s) => (
            <button
              key={s.action}
              onClick={() => runAction(s.action)}
              title={s.label}
              data-testid={`copilot-sidebar-${s.action}`}
              className={`w-full flex items-center gap-2.5 rounded-xl text-[12px] font-semibold text-slate-300 hover:text-white hover:bg-white/[0.06] transition-colors h-10 ${isCollapsed ? "justify-center" : "px-3"}`}
            >
              <s.icon className="h-4 w-4 shrink-0 text-violet-300" />
              {!isCollapsed && <span className="whitespace-nowrap">{s.label}</span>}
            </button>
          ))}
        </nav>
        {!isCollapsed && (
          <div className="p-3 shrink-0 text-[10px] text-slate-600 whitespace-nowrap overflow-hidden inline-flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-violet-400/60" /> Junago Copilot — poste de pilotage IA
          </div>
        )}
      </aside>

      {/* ── Conversation ── */}
      <div className="flex-1 min-w-0 relative">
        <div aria-hidden className="absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-violet-600/20 blur-[120px] pointer-events-none" />
        <div aria-hidden className="absolute -bottom-40 right-1/4 h-80 w-80 rounded-full bg-fuchsia-600/10 blur-[120px] pointer-events-none" />
        <div className="relative h-full max-w-3xl mx-auto">
          <Conversation onTasksChanged={onTasksChanged} />
        </div>
      </div>
    </div>
  );
}
